import { RelatedKeywordTip } from '@/@types/search'
import SearchOutlineIcon from '@/assets/search-outline.svg?react'
import MenuItem from '@/components/DropdownMenu/Item'
import { RELATED_KEYWORD_TIP } from '@/constants/api'
import { getKeywordTip } from '@/utils/fetchers/search'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import parseJikeURLScheme, { JikeTopicURLScheme } from '@/utils/jikeURLScheme'
import queryClient from '@/utils/queryClient'
import { Autocomplete, AutocompleteProps } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import debounce from 'debounce-promise'
import { isEmpty } from 'lodash-es'
import { AnimatePresence } from 'motion/react'
import { useState } from 'react'
import { MenuItemData } from '../DropdownMenu/types'
import styles from './SearchBar.module.css'

interface Tip extends MenuItemData, Pick<RelatedKeywordTip, 'type'> {}

const debouncedGetKeywordTip = debounce(getKeywordTip, 500)

export interface SearchBarProps {
	defaultValue?: string
}
export default function SearchBar({ defaultValue }: SearchBarProps) {
	const [isCompositing, setIsCompositing] = useState(false)
	const [isFocused, setIsFocused] = useState(false)
	const [keywords, setKeywords] = useState<string>(defaultValue || '')
	const [isSubmitted, setIsSubmitted] = useState(false)

	const keywordsQueryOptionsKey = [RELATED_KEYWORD_TIP, keywords]
	const { data: tipOptions = [] } = useQuery({
		queryKey: keywordsQueryOptionsKey,
		queryFn: () => {
			if (isEmpty(keywords)) {
				return []
			}
			return debouncedGetKeywordTip(keywords!).then(
				(data) =>
					data
						.filter((item) => ['topic', 'keyword'].includes(item.type))
						.map((item) => ({
							id: item.url,
							iconURL: item.icon,
							title: item.suggestion,
							type: item.type,
						})) as Tip[]
			)
		},
		enabled: !isEmpty(keywords) && !isCompositing,
	})
	const navigate = useUniversalNavigate()

	const renderOption: AutocompleteProps['renderOption'] = ({ option, checked }) => {
		const index = tipOptions.findIndex((item) => item.title === option.value)

		if (index === -1) {
			return null
		}
		const itemData = tipOptions[index]
		return (
			<AnimatePresence>
				<MenuItem data={itemData} isActive={checked} />
			</AnimatePresence>
		)
	}
	const handlePostSubmit = () => {
		queryClient.setQueryData(keywordsQueryOptionsKey, [])
		setIsSubmitted(true)
	}
	const handleOptionSubmit: AutocompleteProps['onOptionSubmit'] = (value) => {
		const index = tipOptions.findIndex((item) => item.title === value)
		const itemData = tipOptions[index]

		setIsSubmitted(true)

		switch (itemData.type) {
			case 'topic':
				{
					const url = itemData.id
					const { type, id } = parseJikeURLScheme(url) as JikeTopicURLScheme
					if (type !== 'TOPIC') {
						return
					}
					navigate({
						to: '/topic/$id',
						params: {
							id,
						},
					})
				}
				break
			case 'keyword':
				navigate({
					to: '/search',
					search: {
						q: itemData.title,
					},
				})
				break
		}
		handlePostSubmit()
	}
	const tipsData = tipOptions.map((item) => ({ ...item, value: item.title }))
	const hasTipsData = !isEmpty(tipsData)
	const isDropdownOpened = !isSubmitted && isFocused && hasTipsData

	return (
		<div className={styles.container}>
			<Autocomplete
				unstyled
				value={keywords}
				data={tipsData}
				dropdownOpened={isDropdownOpened}
				renderOption={renderOption}
				onCompositionStart={() => {
					setIsCompositing(true)
				}}
				onCompositionEnd={() => {
					setIsCompositing(false)
				}}
				onChange={(nextKeywords) => {
					setKeywords(nextKeywords)
					setIsSubmitted(false)
				}}
				onOptionSubmit={handleOptionSubmit}
				onKeyDown={(e) => {
					if (e.key === 'Enter' && keywords) {
						navigate({
							to: '/search',
							search: {
								q: keywords,
							},
						})
						handlePostSubmit()
					}
				}}
				onFocus={() => void setIsFocused(true)}
				onBlur={() => void setIsFocused(false)}
				withScrollArea={false}
				placeholder="搜索"
				clearable
				leftSection={<SearchOutlineIcon className={styles.icon} />}
				clearButtonProps={{
					className: 'clear-button',
				}}
				classNames={{
					dropdown: 'search-tips-dropdown',
					root: styles.root,
				}}
				styles={{
					dropdown: {
						position: 'absolute',
						left: 0,
						padding: '4px',
						borderRadius: '10px',
						background: 'var(--bg-on-body-1, #fff)',
						fontSize: '14px',
						lineHeight: '20px',
						boxShadow: '0px 2px 12px 0px rgba(0, 0, 0, 0.24)',
						width: '100%',
						maxWidth: 'var(--page-container-width)',
					},
				}}
				comboboxProps={{
					withinPortal: false,
					width: '100%',
					offset: 16,
				}}
			/>
		</div>
	)
}
